import { useEffect, useRef, useState } from 'react'
import type { TextAreaRef } from 'antd/es/input/TextArea'
import { Alert, Input, Modal } from 'antd'
import { useTranslation } from 'react-i18next'
import { MAX_COMPOSE_INSTRUCTIONS_LENGTH } from '../../lib/shared/cleoCompose'

interface ChatwootCleoComposeModalProps {
  open: boolean
  // A draft is being written. The dialog stays open and locked meanwhile, so
  // the request cannot be edited under a run that already took it.
  generating: boolean
  // The last attempt failed. The request stays in the box for a retry.
  failed: boolean
  onSubmit: (instructions: string) => void
  onCancel: () => void
}

/**
 * Asks the advisor what the message should say, for Cleo to write it (CL-2217).
 *
 * Counterpart to the regenerate dialog, for when there is no draft to rework:
 * the advisor is writing first, so the instruction is the whole request. Purely
 * presentational — the caller runs Cleo and decides where the text lands.
 */
export function ChatwootCleoComposeModal({
  open,
  generating,
  failed,
  onSubmit,
  onCancel,
}: ChatwootCleoComposeModalProps) {
  const { t } = useTranslation()
  const [instructions, setInstructions] = useState('')
  const textAreaRef = useRef<TextAreaRef>(null)

  // Each opening starts from an empty request, so an earlier one is not sent
  // again by accident. A failed attempt keeps its text because the dialog never
  // closes in between.
  useEffect(() => {
    if (!open) {
      return
    }
    setInstructions('')
    const timer = window.setTimeout(() => textAreaRef.current?.focus(), 50)
    return () => window.clearTimeout(timer)
  }, [open])

  const trimmed = instructions.trim()

  return (
    <Modal
      title={t('frontend:chatwoot.cleo.composeTitle')}
      open={open}
      okText={t('frontend:chatwoot.cleo.composeConfirm')}
      cancelText={t('frontend:chatwoot.cleo.composeCancel')}
      okButtonProps={{ disabled: !trimmed, loading: generating }}
      cancelButtonProps={{ disabled: generating }}
      // Closing mid-run would hide a draft that is about to land.
      maskClosable={!generating}
      keyboard={!generating}
      closable={!generating}
      onOk={() => onSubmit(trimmed)}
      onCancel={onCancel}
      destroyOnClose
    >
      <p style={{ marginTop: 0 }}>{t('frontend:chatwoot.cleo.composeHint')}</p>
      <Input.TextArea
        ref={textAreaRef}
        value={instructions}
        onChange={(event) => setInstructions(event.target.value)}
        // Cmd/Ctrl+Enter submits, as in the composers; a bare Enter is a new
        // line so a longer request can be written.
        onKeyDown={(event) => {
          if (
            event.key === 'Enter' &&
            (event.metaKey || event.ctrlKey) &&
            !(event.nativeEvent as globalThis.KeyboardEvent).isComposing &&
            trimmed &&
            !generating
          ) {
            event.preventDefault()
            onSubmit(trimmed)
          }
        }}
        placeholder={t('frontend:chatwoot.cleo.composePlaceholder')}
        maxLength={MAX_COMPOSE_INSTRUCTIONS_LENGTH}
        showCount
        disabled={generating}
        autoSize={{ minRows: 4, maxRows: 10 }}
      />
      {failed && !generating && (
        <Alert
          type="error"
          showIcon
          style={{ marginTop: 12 }}
          message={t('frontend:chatwoot.cleo.composeFailed')}
        />
      )}
    </Modal>
  )
}
